/**
 * ============================================================================
 * MAIN JAVASCRIPT ENTRY POINT
 * ============================================================================
 *
 * Main JavaScript file for custom Horizon theme extensions.
 * This file initializes all custom modules and components.
 *
 * @version 2.0.0
 * @author DeSight Studio GmbH
 *
 * ============================================================================
 */

// Note: All imports are removed for browser compatibility
// All classes are available globally after concatenation




/**
 * Main application class
 */
class App {
  constructor() {
    this.eventBus = new EventBus();
    this.modules = new Map();
    this.components = new Map();

    this.init();
  }

  /**
   * Initialize the application
   */
  init() {
    ready(() => {
      // Wait for custom theme settings to load
      if (window.CustomThemeSettings) {
        this.initWithSettings();
      } else {
        document.addEventListener('custom-theme:settings-loaded', () => {
          this.initWithSettings();
        });
      }
    });
  }

  /**
   * Initialize with theme settings
   */
  initWithSettings() {
    this.settings = window.CustomThemeSettings;
    console.log('⚙️ Theme settings loaded:', this.settings);

    this.initModules();
    this.initComponents();
    this.bindEvents();
    this.initPerformanceFeatures();

    // Emit app ready event
    this.eventBus.emit('app:ready', { settings: this.settings });

    console.log('🚀 DeSight Shopify X v1.0 initialized');
  }

  /**
   * Initialize modules
   */
  initModules() {
    // Theme manager
    this.modules.set('theme', new ThemeManager());

    // Modal manager
    this.modules.set('modal', new ModalManager());

    // Form validator
    this.modules.set('validator', new FormValidator());

    // Age Gate manager (DISABLED - using section-based implementation instead)
    // if (typeof AgeGateManager !== 'undefined') {
    //   this.modules.set('ageGate', new AgeGateManager());
    // }

    // Color system (advanced color features)
    if (this.settings.features?.livePreview || this.settings.features?.contrastChecker ||
      this.settings.features?.colorPaletteGenerator !== 'disabled') {
      this.modules.set('colorSystem', new ColorSystem());
    }

    // Live preview system
    if (this.settings.features?.livePreview) {
      this.modules.set('livePreview', new LivePreview());
    }

    // Collection description formatter
    this.modules.set('descriptionFormatter', new DescriptionFormatter());
  }

  /**
   * Initialize components
   */
  initComponents() {
    // Custom buttons
    document.querySelectorAll('[data-custom-button]').forEach(element => {
      this.components.set(element, new CustomButton(element));
    });

    // Product cards
    document.querySelectorAll('[data-product-card]').forEach(element => {
      this.components.set(element, new ProductCard(element));
    });

    // Navigation menus
    document.querySelectorAll('[data-navigation-menu]').forEach(element => {
      this.components.set(element, new NavigationMenu(element));
    });
  }

  /**
   * Bind global events
   */
  bindEvents() {
    // Handle window resize
    window.addEventListener('resize', throttle(() => {
      this.eventBus.emit('window:resize');
    }, 250));

    // Handle scroll
    window.addEventListener('scroll', throttle(() => {
      this.eventBus.emit('window:scroll');
    }, 100));

    // Handle theme changes
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', (e) => {
        this.eventBus.emit('theme:change', { isDark: e.matches });
      });
    }

    // Handle Shopify events
    document.addEventListener('shopify:section:load', (event) => {
      this.handleSectionLoad(event.detail.sectionId);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      this.handleSectionUnload(event.detail.sectionId);
    });

    // Initialize smooth anchor scrolling
    if (typeof initSmoothAnchorScrolling === 'function') {
      initSmoothAnchorScrolling({
        offset: -150, // 150px offset nach oben (für Fixed Header + Abstand)
        updateHash: true,
        initialLoadDelay: 1000, // 1 Sekunde warten nach Page Load für Third-Party-Scripts
        stabilityTimeout: 5000, // Max 5 Sekunden auf Element-Stabilität warten
        onScroll: (targetElement, link) => {
          // Emit event for tracking or other purposes
          this.eventBus.emit('anchor:scroll', { targetElement, link });
          console.log('✅ Scrolled to element:', targetElement.id);
        }
      });
    }
  }

  /**
   * Handle section load (for theme editor)
   */
  handleSectionLoad(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (section) {
      // Re-initialize components in the loaded section
      this.initComponentsInSection(section);
      this.eventBus.emit('section:load', { sectionId, section });
    }
  }

  /**
   * Handle section unload (for theme editor)
   */
  handleSectionUnload(sectionId) {
    // Clean up components in the unloaded section
    this.cleanupComponentsInSection(sectionId);
    this.eventBus.emit('section:unload', { sectionId });
  }

  /**
   * Initialize components in a specific section
   */
  initComponentsInSection(section) {
    // Custom buttons
    section.querySelectorAll('[data-custom-button]').forEach(element => {
      if (!this.components.has(element)) {
        this.components.set(element, new CustomButton(element));
      }
    });

    // Product cards
    section.querySelectorAll('[data-product-card]').forEach(element => {
      if (!this.components.has(element)) {
        this.components.set(element, new ProductCard(element));
      }
    });

    // Navigation menus
    section.querySelectorAll('[data-navigation-menu]').forEach(element => {
      if (!this.components.has(element)) {
        this.components.set(element, new NavigationMenu(element));
      }
    });
  }

  /**
   * Clean up components in a specific section
   */
  cleanupComponentsInSection(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (section) {
      // Find and destroy components in this section
      this.components.forEach((component, element) => {
        if (section.contains(element)) {
          if (typeof component.destroy === 'function') {
            component.destroy();
          }
          this.components.delete(element);
        }
      });
    }
  }

  /**
   * Get a module by name
   */
  getModule(name) {
    return this.modules.get(name);
  }

  /**
   * Get the event bus
   */
  getEventBus() {
    return this.eventBus;
  }

  /**
   * Initialize performance features based on settings
   */
  initPerformanceFeatures() {
    if (!this.settings) return;

    // Initialize Web Vitals tracking
    if (this.settings.features.webVitalsTracking && typeof initPerformanceMonitoring === 'function') {
      initPerformanceMonitoring();
    }

    // Initialize lazy loading
    if (this.settings.features.lazyLoading && typeof lazyLoadImages === 'function') {
      lazyLoadImages();
    }

    // Preload critical resources
    if (this.settings.features.preloadCriticalResources && typeof ResourceHints === 'function') {
      ResourceHints.preconnect('https://cdn.shopify.com');
      ResourceHints.dnsPrefetch('https://fonts.googleapis.com');
    }

    // Apply performance mode settings
    if (this.settings.features.performanceMode) {
      document.documentElement.classList.add('performance-mode');
    }
  }
}

// Initialize the application
const app = new App();

// Make app globally available for debugging
if (typeof window !== 'undefined') {
  window.CustomThemeApp = app;
}

// Add compatibility layer for third-party scripts
window.addEventListener('error', (event) => {
  // Suppress Growave errors that don't affect our framework
  if (event.filename && event.filename.includes('mBhmrhArhzk8L2OyGtue1BEH8ihIMkje')) {
    console.warn('🔇 Suppressed Growave error:', event.message);
    event.preventDefault();
    return false;
  }
});


